/**
 * Fill-only planner for kyc_profiles/{uid} and its documents / screening / contracts.
 *
 * Contract: a field is written only when it is empty on the stored document
 * (undefined, null, "", [], {} or a provenance object whose value is empty).
 * Nothing is overwritten and nothing is deleted. The only field rewritten on every
 * fill is `kyc_last_fill`, which records who filled which field NAMES (never values).
 *
 * Pure module: no Firebase imports. The service layer reads the documents, calls
 * buildKycFillPlan and writes plan.profile / plan.subdocs inside a transaction.
 */
import {
  KYC_STATUS,
  SCREENING_STATUS,
  KYC_DOC_STATUS,
  KYC_DOC_TYPES,
  CLIENT_CLASSIFICATION,
  LEGACY_DOCUMENT_TYPE_MAP,
  CONFIDENCE,
  ACCOUNT_STATE,
} from "./kycConstants";
import {
  buildComplianceChecklist,
  computeCompletenessScore,
  deriveKycStatusFromDocs,
  nextReviewDateFromRisk,
} from "./kycCompliance";

export const IDENTITY_DOC_ID = "users_document_identity";
export const REQUIRED_IDENTITY_DOC_ID = "required_identity";
export const SCREENING_DOC_ID = "primary";
export const CONTRACT_DOC_ID = "primary";

const ID_DOC_TYPES = new Set([
  KYC_DOC_TYPES.PASSPORT,
  KYC_DOC_TYPES.NATIONAL_ID,
  KYC_DOC_TYPES.DRIVERS_LICENSE,
]);

function isPlainObject(v) {
  return v !== null && typeof v === "object" && Object.getPrototypeOf(v) === Object.prototype;
}

function isProvenanceField(v) {
  return (
    isPlainObject(v) &&
    Object.prototype.hasOwnProperty.call(v, "value") &&
    ("confidence" in v || "source_method" in v || "extracted_at" in v)
  );
}

export function isEmptyValue(v) {
  if (v === undefined || v === null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (isProvenanceField(v)) return isEmptyValue(v.value);
  if (isPlainObject(v)) return Object.keys(v).length === 0;
  return false;
}

/** Keys of `candidate` that are non-empty and empty on `existing`. */
export function fillOnlyPatch(existing, candidate) {
  const patch = {};
  const filled = [];
  const kept = [];
  for (const [key, value] of Object.entries(candidate || {})) {
    if (isEmptyValue(value)) continue;
    if (isEmptyValue(existing ? existing[key] : undefined)) {
      patch[key] = value;
      filled.push(key);
    } else {
      kept.push(key);
    }
  }
  return { patch, filled, kept };
}

/**
 * Automatic kyc_status changes are only draft / in_review / missing_docs
 * moving to expired or missing_docs. Approved and rejected stay as the admin left them.
 */
export function nextAutomaticKycStatus(current, derived) {
  if (current === KYC_STATUS.APPROVED || current === KYC_STATUS.REJECTED) return null;
  if (
    current !== KYC_STATUS.DRAFT &&
    current !== KYC_STATUS.IN_REVIEW &&
    current !== KYC_STATUS.MISSING_DOCS
  ) {
    return null;
  }
  if (derived !== KYC_STATUS.EXPIRED && derived !== KYC_STATUS.MISSING_DOCS) return null;
  if (current === derived) return null;
  return derived;
}

function firstFilled(...values) {
  for (const value of values) {
    if (!isEmptyValue(value)) return value;
  }
  return null;
}

/**
 * What an OCR scan may write. A filled number, expiry, photo or birth date is never
 * replaced. A different OCR reading is returned for the screen and is not part of the patch.
 * A new photo URL becomes its own document; the stored identity document is not the target.
 */
export function identityScanPlan({ profile, identityDoc, userBirthDate, storageUrl, extracted }) {
  const idNumber = isEmptyValue(extracted?.document_number) ? null : String(extracted.document_number).trim();
  const expiry = isEmptyValue(extracted?.expiry_date) ? null : String(extracted.expiry_date).trim();
  const dob = isEmptyValue(extracted?.date_of_birth) ? null : String(extracted.date_of_birth).trim();
  const differences = [];

  const note = (field, extractedValue, storedValue) => {
    if (isEmptyValue(extractedValue) || isEmptyValue(storedValue)) return false;
    if (String(storedValue).trim() === String(extractedValue).trim()) return false;
    differences.push({ field, extracted: String(extractedValue) });
    return true;
  };

  const storedNumber = firstFilled(
    profile?.identity_document_number,
    identityDoc?.id_number,
    identityDoc?.document_number,
    profile?.users_document_number
  );
  const storedExpiry = firstFilled(
    profile?.identity_expiry_date,
    identityDoc?.expiry_date,
    profile?.document_expiry_date
  );
  const storedDob = firstFilled(profile?.birth_date, userBirthDate);
  const numberDiff = note("identity_document_number", idNumber, storedNumber);
  const expiryDiff = note("identity_expiry_date", expiry, storedExpiry);
  const dobDiff = note("birth_date", dob, storedDob);

  const storedUrl = identityDoc?.storage_url;
  const urlDiffers = !isEmptyValue(storedUrl) && storedUrl !== storageUrl;
  const targetDocId = urlDiffers ? alternateIdentityDocId(storageUrl) : IDENTITY_DOC_ID;
  const docPayload = {};
  if (urlDiffers && !isEmptyValue(storageUrl)) {
    docPayload.storage_url = storageUrl;
    docPayload.status = KYC_DOC_STATUS.NEEDS_REVIEW;
    docPayload.doc_type = identityDoc?.doc_type || KYC_DOC_TYPES.PASSPORT;
    docPayload.label = "Identity document";
  } else if (isEmptyValue(storedUrl) && !isEmptyValue(storageUrl)) {
    docPayload.storage_url = storageUrl;
    if (isEmptyValue(identityDoc?.status)) docPayload.status = KYC_DOC_STATUS.NEEDS_REVIEW;
    if (isEmptyValue(identityDoc?.doc_type)) docPayload.doc_type = KYC_DOC_TYPES.PASSPORT;
  }
  const sameDoc = !urlDiffers;
  if (!numberDiff && idNumber && (urlDiffers || (sameDoc && isEmptyValue(identityDoc?.id_number)))) {
    docPayload.id_number = idNumber;
  }
  if (!numberDiff && idNumber && (urlDiffers || (sameDoc && isEmptyValue(identityDoc?.document_number)))) {
    docPayload.document_number = idNumber;
  }
  if (!expiryDiff && expiry && (urlDiffers || (sameDoc && isEmptyValue(identityDoc?.expiry_date)))) {
    docPayload.expiry_date = expiry;
  }

  const profileOverrides = {};
  if (!numberDiff && idNumber && isEmptyValue(profile?.identity_document_number)) {
    profileOverrides.identity_document_number = idNumber;
  }
  if (!numberDiff && idNumber && isEmptyValue(profile?.users_document_number)) {
    profileOverrides.users_document_number = idNumber;
  }
  if (!expiryDiff && expiry && isEmptyValue(profile?.identity_expiry_date)) {
    profileOverrides.identity_expiry_date = expiry;
  }
  if (!dobDiff && dob && isEmptyValue(profile?.birth_date)) {
    profileOverrides.birth_date = dob;
  }

  return {
    differences,
    profileOverrides,
    document: Object.keys(docPayload).length ? { id: targetDocId, payload: docPayload } : null,
    fillUserBirthDate: Boolean(!dobDiff && dob && isEmptyValue(userBirthDate)),
    dob: !dobDiff && dob && isEmptyValue(userBirthDate) ? dob : null,
  };
}

export function parseDateLoose(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const isoDate = raw.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (isoDate) return `${isoDate[1]}-${isoDate[2]}-${isoDate[3]}`;

  const dmy = raw.match(/\b(\d{2})[/.-](\d{2})[/.-](\d{4})\b/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  return null;
}

/** Stable short id for a URL (used when the registration photo changed). */
export function shortHash(text) {
  let h = 5381;
  const s = String(text || "");
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

export function alternateIdentityDocId(storageUrl) {
  return `${IDENTITY_DOC_ID}_${shortHash(storageUrl)}`;
}

function mapDocType(documentType) {
  return (
    LEGACY_DOCUMENT_TYPE_MAP[documentType] ||
    LEGACY_DOCUMENT_TYPE_MAP[Number(documentType)] ||
    KYC_DOC_TYPES.PASSPORT
  );
}

function provenance(value, meta = {}) {
  return {
    value: value ?? null,
    confidence: meta.confidence || CONFIDENCE.MEDIUM,
    source_file: meta.source_file || null,
    source_method: meta.source_method || "import",
    extracted_at: meta.extracted_at || null,
  };
}

/**
 * Which documents/{id} ids the plan may touch. The service reads exactly these
 * (inside the transaction) before calling buildKycFillPlan.
 */
export function documentIdsToRead(user, overrides = {}) {
  const ids = new Set([IDENTITY_DOC_ID, REQUIRED_IDENTITY_DOC_ID]);
  if (user?.document) ids.add(alternateIdentityDocId(user.document));
  for (const d of overrides.documents || []) {
    if (d?.id) ids.add(d.id);
  }
  return [...ids];
}

function profileCandidate({ uid, user, overrides, matchMethod, isOrphan }) {
  const o = overrides.profile || {};
  const pick = (fromUser, fromOverride) => (isEmptyValue(fromUser) ? fromOverride : fromUser);
  const classification = user
    ? user.user_type === 1
      ? CLIENT_CLASSIFICATION.CORPORATE
      : CLIENT_CLASSIFICATION.INDIVIDUAL
    : o.classification;

  return {
    user_id: uid,
    display_name: pick(user?.full_name, o.display_name),
    email: pick(user?.email, o.email),
    phone: pick(user?.phone, o.phone),
    classification: pick(classification, o.classification),
    app_user_status: user ? user.status ?? null : null,
    account_state: o.account_state || (user?.status === 1 ? ACCOUNT_STATE.ACTIVE : undefined),
    folder_name: o.folder_name,
    folder_path: o.folder_path,
    client_address: o.client_address,
    identity_document_number: o.identity_document_number,
    identity_expiry_date: o.identity_expiry_date,
    birth_date: o.birth_date,
    users_document_url: user?.document,
    users_document_back_url: user?.document_back,
    users_document_number: pick(user?.document_number, o.users_document_number),
    contract_summary: o.contract_summary,
    match_method: matchMethod,
    match_confidence: o.match_confidence || CONFIDENCE.HIGH,
    is_orphan: isOrphan ? true : undefined,
  };
}

function identityCandidate(user) {
  if (!user?.document) return null;
  return {
    doc_id: IDENTITY_DOC_ID,
    doc_type: mapDocType(user.document_type),
    status: KYC_DOC_STATUS.NEEDS_REVIEW,
    storage_url: user.document,
    source: "users.document",
    label: "Identity uploaded at registration",
    expiry_date: parseDateLoose(user.document_expiry_date),
    id_number: user.document_number,
    document_number: user.document_number,
    source_method: "users_profile",
  };
}

function contractCandidate(contractMeta, overrideContract) {
  const cm = contractMeta || null;
  const oc = overrideContract || null;
  if (!cm && !oc) return null;
  const meta = cm
    ? { source_method: "contract_metadata", confidence: CONFIDENCE.MEDIUM }
    : { source_method: "folder_scan", confidence: oc.confidence || CONFIDENCE.LOW, source_file: oc.source_file };
  const val = (cmKey, ocKey) => (cm && !isEmptyValue(cm[cmKey]) ? cm[cmKey] : oc ? oc[ocKey] : null);
  return {
    contract_id: CONTRACT_DOC_ID,
    signature_date: provenance(val("contract_open_date", "signature_date"), meta),
    initial_amount: provenance(val("initial_deposit_amount", "initial_amount"), meta),
    contract_wallet: provenance(val("first_wallet_address", "contract_wallet"), meta),
    fiddux_wallet_disclosed: provenance(val("fiddux_internal_wallet_address", "fiddux_wallet_disclosed"), meta),
    client_address: provenance(oc ? oc.client_address : null, meta),
    withdrawal_wallets: (cm && cm.withdrawal_wallet_addresses) || [],
    source: cm ? "contract_metadata" : "folder_scan",
  };
}

function contractSummaryFromMeta(contractMeta) {
  if (!contractMeta) return undefined;
  return {
    signature_date: contractMeta.contract_open_date || null,
    contract_open_date: contractMeta.contract_open_date || null,
    initial_amount: contractMeta.initial_deposit_amount ?? null,
    contract_wallet: contractMeta.first_wallet_address || null,
    fiddux_wallet_disclosed: contractMeta.fiddux_internal_wallet_address || null,
    withdrawal_wallets: contractMeta.withdrawal_wallet_addresses || [],
    confidence: CONFIDENCE.MEDIUM,
    source: "contract_metadata",
  };
}

function subdocStep(collection, id, existing, candidate) {
  if (!candidate) return null;
  const exists = !!existing;
  const { patch, filled, kept } = fillOnlyPatch(existing || {}, candidate);
  return { collection, id, exists, patch, filled, kept };
}

/**
 * @param {object} input
 * @param {string} input.uid kyc_profiles document id
 * @param {object|null} input.user users/{uid} mapped (mapUserDoc shape) or null for orphans
 * @param {object|null} input.profile current kyc_profiles/{uid} data or null
 * @param {object} input.documents map docId -> data|null for documentIdsToRead()
 * @param {object[]} input.allDocuments every documents/* (for derived compliance only)
 * @param {object|null} input.screening screening/primary data or null
 * @param {object|null} input.contract contracts/primary data or null
 * @param {object|null} input.contractMeta contract_metadata/{uid} data or null
 * @param {object} [input.overrides] { profile, documents: [{id, payload}], contract }
 * @param {boolean} [input.allowCreate=true] create kyc_profiles/{uid} when missing
 * @param {boolean} [input.isOrphan=false]
 * @param {string} input.actorUid admin uid (stored, never an e-mail)
 * @param {string} input.source action name stored in kyc_last_fill
 * @param {string} [input.matchMethod]
 * @param {*} input.stamp server timestamp sentinel (or Date in tests)
 * @param {Date} [input.now]
 */
export function buildKycFillPlan(input) {
  const {
    uid,
    user = null,
    profile = null,
    documents = {},
    allDocuments = [],
    screening = null,
    contract = null,
    contractMeta = null,
    overrides = {},
    allowCreate = true,
    isOrphan = false,
    actorUid = null,
    source = "kyc_fill",
    matchMethod = "firebase_user",
    stamp = null,
    now = new Date(),
  } = input;

  const profileExists = !!profile;
  const empty = {
    uid,
    profileExists,
    createProfile: false,
    profile: { patch: {}, filled: [], kept: [] },
    subdocs: [],
    filledFields: [],
    totalFilled: 0,
    signature: `${uid}|none`,
    skippedReason: null,
  };
  if (!uid) return { ...empty, skippedReason: "missing_uid" };
  if (!user && !isOrphan) return { ...empty, skippedReason: "user_not_found" };
  if (!profileExists && !allowCreate) return { ...empty, skippedReason: "no_profile_create_not_allowed" };

  // Base profile fields
  const baseCandidate = profileCandidate({ uid, user, overrides, matchMethod, isOrphan });
  if (isEmptyValue(baseCandidate.contract_summary)) {
    baseCandidate.contract_summary = contractSummaryFromMeta(contractMeta);
  }
  const base = fillOnlyPatch(profile || {}, baseCandidate);

  // Sub-documents
  const subdocs = [];
  const identity = identityCandidate(user);
  const storedIdentity = documents[IDENTITY_DOC_ID] || null;
  if (identity) {
    const storedUrl = storedIdentity?.storage_url;
    if (!isEmptyValue(storedUrl) && storedUrl !== identity.storage_url) {
      const altId = alternateIdentityDocId(identity.storage_url);
      subdocs.push(
        subdocStep("documents", altId, documents[altId] || null, {
          ...identity,
          doc_id: altId,
          label: "Registration photo (changed since KYC import)",
        })
      );
    } else {
      subdocs.push(subdocStep("documents", IDENTITY_DOC_ID, storedIdentity, identity));
    }
  }

  for (const d of overrides.documents || []) {
    if (!d?.id || !d.payload) continue;
    subdocs.push(subdocStep("documents", d.id, documents[d.id] || null, { ...d.payload, doc_id: d.id }));
  }

  const hasAnyIdentitySource =
    !!identity ||
    !isEmptyValue(profile?.users_document_url) ||
    allDocuments.some(
      (d) => ID_DOC_TYPES.has(d.doc_type) && d.status !== KYC_DOC_STATUS.MISSING && d.id !== REQUIRED_IDENTITY_DOC_ID
    ) ||
    (overrides.documents || []).some((d) => ID_DOC_TYPES.has(d?.payload?.doc_type));
  if (!hasAnyIdentitySource && !documents[REQUIRED_IDENTITY_DOC_ID]) {
    subdocs.push(
      subdocStep("documents", REQUIRED_IDENTITY_DOC_ID, null, {
        doc_id: REQUIRED_IDENTITY_DOC_ID,
        doc_type: KYC_DOC_TYPES.PASSPORT,
        status: KYC_DOC_STATUS.MISSING,
        label: "Identity document required",
      })
    );
  }

  if (!screening) {
    subdocs.push(
      subdocStep("screening", SCREENING_DOC_ID, null, {
        check_id: SCREENING_DOC_ID,
        status: SCREENING_STATUS.PENDING,
        pep_checked: false,
        sanctions_checked: false,
        notes: "Awaiting manual or automated screening",
      })
    );
  }

  const contractCand = contractCandidate(contractMeta, overrides.contract);
  if (contractCand) subdocs.push(subdocStep("contracts", CONTRACT_DOC_ID, contract, contractCand));

  const steps = subdocs.filter(Boolean);

  // Derived compliance fields, computed on the merged view; also fill-only.
  const mergedProfile = { ...(profile || {}), ...base.patch };
  const docsById = new Map(allDocuments.map((d) => [d.id, d]));
  for (const s of steps) {
    if (s.collection !== "documents") continue;
    const prev = docsById.get(s.id) || documents[s.id] || {};
    docsById.set(s.id, { ...prev, ...s.patch, id: s.id });
  }
  const mergedDocs = [...docsById.values()];
  const screeningStep = steps.find((s) => s.collection === "screening");
  const mergedScreening = screening || (screeningStep ? screeningStep.patch : null);
  const contractStep = steps.find((s) => s.collection === "contracts");
  const mergedContract = contractStep
    ? Object.fromEntries(
        Object.entries({ ...(contract || {}), ...contractStep.patch }).map(([k, v]) => [
          k,
          isProvenanceField(v) ? v.value : v,
        ])
      )
    : null;

  const checklist = buildComplianceChecklist(mergedProfile, mergedDocs, mergedScreening, mergedContract);
  const identityDoc =
    mergedDocs.find((d) => d.id === IDENTITY_DOC_ID) ||
    mergedDocs.find((d) => ID_DOC_TYPES.has(d.doc_type) && d.status !== KYC_DOC_STATUS.MISSING);
  const derivedStatus = deriveKycStatusFromDocs(mergedProfile, mergedDocs, mergedScreening) || KYC_STATUS.DRAFT;
  const derivedCandidate = {
    screening_status: mergedScreening?.status || SCREENING_STATUS.PENDING,
    risk_rating: "medium",
    compliance_checklist: checklist,
    completeness_score: computeCompletenessScore(checklist),
    next_review_at: nextReviewDateFromRisk(mergedProfile.risk_rating || "medium", now),
    identity_document_number:
      identityDoc?.id_number || identityDoc?.document_number || user?.document_number || null,
    identity_expiry_date: identityDoc?.expiry_date || null,
  };
  if (!profileExists) derivedCandidate.kyc_status = derivedStatus;
  const derived = fillOnlyPatch(mergedProfile, derivedCandidate);

  const profilePatch = { ...base.patch, ...derived.patch };
  const profileFilled = [...base.filled, ...derived.filled];
  if (profileExists) {
    const nextStatus = nextAutomaticKycStatus(profile?.kyc_status, derivedStatus);
    if (nextStatus) {
      profilePatch.kyc_status = nextStatus;
      profileFilled.push("kyc_status");
    }
  }
  const filledFields = [
    ...profileFilled,
    ...steps.flatMap((s) => s.filled.map((f) => `${s.collection}/${s.id}.${f}`)),
  ];
  const totalFilled = filledFields.length;
  const createProfile = !profileExists && totalFilled > 0;

  if (totalFilled > 0) {
    if (createProfile) {
      profilePatch.kyc_created_by = actorUid;
      profilePatch.kyc_created_at = stamp;
      profilePatch.updated_at = stamp;
    }
    profilePatch.kyc_last_fill = {
      by_uid: actorUid,
      at: stamp,
      source,
      fields: filledFields,
    };
  }

  const signature = [
    uid,
    createProfile ? "create" : "update",
    ...filledFields.slice().sort(),
    ...steps.filter((s) => s.filled.length && !s.exists).map((s) => `new:${s.collection}/${s.id}`).sort(),
  ].join("|");

  return {
    uid,
    profileExists,
    createProfile,
    profile: {
      patch: profilePatch,
      filled: profileFilled,
      kept: [...base.kept, ...derived.kept],
    },
    subdocs: steps.filter((s) => s.filled.length > 0),
    keptSubdocFields: steps.flatMap((s) => s.kept.map((f) => `${s.collection}/${s.id}.${f}`)),
    filledFields,
    totalFilled,
    signature,
    skippedReason: totalFilled === 0 ? "nothing_to_fill" : null,
  };
}

/**
 * Field label safe for bulk screens: sub-document ids built from folder file names
 * can carry client names, so they are collapsed to "*".
 */
export function genericFieldLabel(field) {
  return String(field).replace(/^(documents|contracts|screening)\/[^.]+\./, "$1/*.");
}

/** Counts / names only — safe to show on screen or log. */
export function summarizeFillPlan(plan) {
  return {
    uid: plan.uid,
    created: !!plan.createProfile,
    filledCount: plan.totalFilled || 0,
    fields: plan.filledFields || [],
    newSubdocs: (plan.subdocs || []).filter((s) => !s.exists).map((s) => `${s.collection}/${s.id}`),
    keptCount: (plan.profile?.kept?.length || 0) + (plan.keptSubdocFields?.length || 0),
    skippedReason: plan.skippedReason || null,
  };
}
